import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { interpolate } from "domains/language/interpolate";
import { getSiteLanguage } from "domains/language/selectors";

const formatDate = (
  date: number | string,
  language = "en-US",
  onlyDate = false,
  showWeekday = true,
  dateFormatOptions = getFormatOptions(onlyDate, showWeekday),
) => new Date(typeof date === "number" ? date * 1000 : date).toLocaleDateString(language, dateFormatOptions);

type DateProps = {
  date: number | string;
  onlyDate?: boolean;
  options?: Intl.DateTimeFormatOptions;
  showWeekday?: boolean;
};

const difference = (firstDate: Date, secondDate: Date, type: "hours" | "days") => {
  const factor = (type === "days" ? 24 : 1) * 60 * 60 * 1000;
  return Math.round((firstDate.getTime() - secondDate.getTime()) / factor);
};

const getFormatOptions = (onlyDate = false, showWeekday = true): Intl.DateTimeFormatOptions => {
  let dateFormatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  if (showWeekday) {
    dateFormatOptions = {
      ...dateFormatOptions,
      weekday: "short",
    };
  }
  if (!onlyDate) {
    dateFormatOptions = {
      ...dateFormatOptions,
      hour: "numeric",
      minute: "numeric",
    };
  }
  return dateFormatOptions;
};

type RelativeDateProps = { date: number; onlyDate?: boolean; precision?: "hour" | "day"; showWeekday?: boolean };

export const useFormatDate = () => {
  const language = useSelector(getSiteLanguage);
  const translate = useTranslate();

  const getDate = ({ date, onlyDate = false, showWeekday = false, options }: DateProps) =>
    formatDate(date, language, onlyDate, showWeekday, options);

  const getString = (diff: number, type: "days" | "hours") => {
    let localizedKey:
      | "app.date.format.difference_in_day"
      | "app.date.format.difference_in_days"
      | "app.date.format.difference_in_future_day"
      | "app.date.format.difference_in_future_days"
      | "app.date.format.difference_in_future_hour"
      | "app.date.format.difference_in_future_hours"
      | "app.date.format.difference_in_hour"
      | "app.date.format.difference_in_hours"
      | "app.now.title";

    const isSingle = Math.abs(diff) !== 1;

    if (diff > 0) {
      if (type === "days") {
        if (isSingle) localizedKey = "app.date.format.difference_in_day";
        else localizedKey = "app.date.format.difference_in_days";
      } else {
        if (isSingle) localizedKey = "app.date.format.difference_in_hour";
        else localizedKey = "app.date.format.difference_in_hours";
      }
    } else {
      if (type === "days") {
        if (isSingle) localizedKey = "app.date.format.difference_in_future_day";
        else localizedKey = "app.date.format.difference_in_future_days";
      } else {
        if (isSingle) localizedKey = "app.date.format.difference_in_future_hour";
        else localizedKey = "app.date.format.difference_in_future_hours";
      }
    }

    let interpolateKeys: Record<string, () => string> = {
      days: () => `${Math.abs(diff)}`,
    };

    if (type === "hours") {
      if (Math.abs(diff) < 1) {
        localizedKey = "app.now.title";
      } else {
        interpolateKeys = { hours: () => `${Math.abs(diff)}` };
      }
    }

    return interpolate(translate(localizedKey), interpolateKeys);
  };

  const formatRelativeDate = (
    timestamp: number,
    locales = "en",
    precision: "hour" | "day" = "hour",
    onlyDate?: boolean,
    showWeekday?: boolean,
  ) => {
    const today = new Date();
    const time = new Date(timestamp * 1000);
    const diffDays = difference(today, time, "days");

    if (Math.abs(diffDays) > 6) {
      return formatDate(timestamp, locales, onlyDate, showWeekday);
    }

    if (Math.abs(diffDays) < 1) {
      if (precision === "day") {
        return translate("app.time.today");
      }

      const diffHours = difference(today, time, "hours");
      return getString(diffHours, "hours");
    }

    return getString(diffDays, "days");
  };

  const getRelativeDate = ({ date, precision = "hour", onlyDate = false, showWeekday = false }: RelativeDateProps) =>
    formatRelativeDate(date, language, precision, onlyDate, showWeekday);

  return { getDate, getRelativeDate };
};

export const getTimestampFromDate = (date: string): number => {
  if (!date) {
    return 0;
  }
  return new Date(date).getTime() / 1000;
};
